import styled, { css } from 'styled-components';

// Media query breakpoints
// const breakpoints = {
//   sm: '576px',
//   md: '768px',
//   lg: '992px',
//   xl: '1200px'
// };

// Media query helper
// const media = Object.keys(breakpoints).reduce((acc, label) => {
//   acc[label] = (...args) => css`
//     @media (min-width: ${breakpoints[label]}) {
//       ${css(...args)}
//     }
//   `;
//   return acc;
// }, {});

// Base Input component
export const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background};
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  box-sizing: border-box;
  
  ${({ hasIcon, theme }) => hasIcon && css`
    padding-right: ${theme.spacing.xl};
  `}

  ${({ size, theme }) => size === 'small' && css`
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.typography.fontSizes.sm};
  `}

  ${({ size, theme }) => size === 'large' && css`
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    font-size: ${theme.typography.fontSizes.lg};
  `}

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(244, 81, 30, 0.2);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.disabled};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.borderLight};
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${({ variant, theme }) => variant === 'error' && css`
    border-color: ${theme.colors.error};
    
    &:focus {
      border-color: ${theme.colors.error};
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
    }
  `}

  ${({ variant, theme }) => variant === 'success' && css`
    border-color: ${theme.colors.success};
    
    &:focus {
      border-color: ${theme.colors.success};
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
    }
  `}
`;

// Input wrapper for icons and additional elements
export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

// Textarea component
export const Textarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}20`};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

// Select component
export const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23${({ theme }) => theme.colors.text.secondary.substring(1)}%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right ${({ theme }) => theme.spacing.sm} center;
  background-size: .65em auto;
  padding-right: ${({ theme }) => `calc(${theme.spacing.sm} * 2 + 1em)`};
  cursor: pointer;

  ${({ size, theme }) => size === 'small' && css`
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    padding-right: calc(${theme.spacing.sm} * 2 + 1em);
    font-size: ${theme.typography.fontSizes.sm};
  `}

  ${({ size, theme }) => size === 'large' && css`
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    padding-right: calc(${theme.spacing.lg} * 2 + 1em);
    font-size: ${theme.typography.fontSizes.lg};
  `}

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(244, 81, 30, 0.2);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.borderLight};
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${({ variant, theme }) => variant === 'error' && css`
    border-color: ${theme.colors.error};
    
    &:focus {
      border-color: ${theme.colors.error};
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
    }
  `}

  ${({ variant, theme }) => variant === 'success' && css`
    border-color: ${theme.colors.success};
    
    &:focus {
      border-color: ${theme.colors.success};
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
    }
  `}
`;

// Checkbox component
export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${({ size }) => size === 'small' && css`
    width: 16px;
    height: 16px;
  `}

  ${({ size }) => size === 'large' && css`
    width: 24px;
    height: 24px;
  `}

  &:checked {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: ${({ size }) => size === 'small' ? '10px' : size === 'large' ? '14px' : '12px'};
    font-weight: bold;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:indeterminate {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:indeterminate::after {
    content: '−';
    color: ${({ theme }) => theme.colors.text.inverse};
    font-weight: bold;
  }
`;

// Radio component
export const Radio = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${({ size }) => size === 'small' && css`
    width: 16px;
    height: 16px;
  `}

  ${({ size }) => size === 'large' && css`
    width: 24px;
    height: 24px;
  `}

  &:checked {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ size }) => size === 'small' ? '6px' : size === 'large' ? '10px' : '8px'};
    height: ${({ size }) => size === 'small' ? '6px' : size === 'large' ? '10px' : '8px'};
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// File input component
export const FileInput = styled.input.attrs({ type: 'file' })`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(244, 81, 30, 0.2);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::file-selector-button {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    border: none;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
    cursor: pointer;
    margin-right: ${({ theme }) => theme.spacing.sm};
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.secondary};
    }
  }
`;

// Range/Slider component
export const Range = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.border};
  outline: none;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: none;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

// Search input component
export const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing.xl};
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>');
  background-repeat: no-repeat;
  background-position: left ${({ theme }) => theme.spacing.sm} center;
  background-size: 16px 16px;
`;

// Password input component
export const PasswordInput = styled(Input)`
  padding-right: ${({ theme }) => theme.spacing.xl};
`;

// Number input component
export const NumberInput = styled(Input).attrs({ type: 'number' })`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

// Form group component
export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};

  ${({ inline }) => inline && css`
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
  `}
`;

// Input group for combining inputs with buttons or text
export const InputGroup = styled.div`
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  ${Input}, ${Select} {
    border: none;
    border-radius: 0;
    flex: 1;

    &:focus {
      box-shadow: none;
    }
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(244, 81, 30, 0.2);
  }
`;

// Input addon for prefixes and suffixes
export const InputAddon = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  white-space: nowrap;
  display: flex;
  align-items: center;
  border-right: ${({ position }) => position === 'left' ? '1px solid' : 'none'} ${({ theme }) => theme.colors.border};
  border-left: ${({ position }) => position === 'right' ? '1px solid' : 'none'} ${({ theme }) => theme.colors.border};
`;

// Floating label component
export const FloatingLabelWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const FloatingLabel = styled.label`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all 0.2s ease;
  pointer-events: none;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 ${({ theme }) => theme.spacing.xs};

  ${({ floating }) => floating && css`
    top: -8px;
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
    color: ${({ theme }) => theme.colors.primary};
  `}
`;

export const FloatingInput = styled(Input)`
  &:focus + ${FloatingLabel},
  &:not(:placeholder-shown) + ${FloatingLabel} {
    top: -8px;
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Input helper text
export const InputHelperText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ variant, theme }) => {
    switch (variant) {
      case 'error': return theme.colors.error;
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.text.secondary;
    }
  }};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// Switch/Toggle component
export const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: ${({ size }) => size === 'small' ? '40px' : size === 'large' ? '60px' : '50px'};
  height: ${({ size }) => size === 'small' ? '20px' : size === 'large' ? '30px' : '25px'};
  cursor: pointer;
`;

export const SwitchInput = styled.input.attrs({ type: 'checkbox' })`
  opacity: 0;
  width: 0;
  height: 0;
`;

export const SwitchSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.border};
  transition: .4s;
  border-radius: ${({ theme }) => theme.borderRadius.pill};

  &:before {
    position: absolute;
    content: "";
    height: ${({ size }) => size === 'small' ? '16px' : size === 'large' ? '26px' : '21px'};
    width: ${({ size }) => size === 'small' ? '16px' : size === 'large' ? '26px' : '21px'};
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }

  ${SwitchInput}:checked + & {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  ${SwitchInput}:focus + & {
    box-shadow: 0 0 1px ${({ theme }) => theme.colors.primary};
  }

  ${SwitchInput}:checked + &:before {
    transform: translateX(${({ size }) => size === 'small' ? '20px' : size === 'large' ? '30px' : '25px'});
  }

  ${SwitchInput}:disabled + & {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;